import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Tooltip,
  message,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import axiosInstance from "../axiosInstance";

function Notice() {
  const [notices, setNotices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [viewNotice, setViewNotice] = useState(null);
  const [form] = Form.useForm();
  const emp = JSON.parse(localStorage.getItem("auth_user"));

  useEffect(() => {
    loadNotices();
    loadEmployees();
  }, []);

  const loadNotices = async () => {
    const res = await axiosInstance.get("/notice_api.php?action=list");
    if (res.data.success) setNotices(res.data.data);
  };

  const loadEmployees = async () => {
    const res = await axiosInstance.post("/all_employee.php");
    if (res.data.success) setEmployees(res.data.data);
  };

  const openCreate = () => {
    setEditingNotice(null);
    form.resetFields();
    setModalVisible(true);
  };

  const openEdit = (record) => {
    setEditingNotice(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const openView = (record) => {
    setViewNotice(record);
    setViewModalVisible(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();

    const payload = {
      ...values,
      created_by: emp.id,
      created_by_name: emp.name,
    };

    let url = editingNotice
      ? "/notice_api.php?action=update"
      : "/notice_api.php?action=create";

    if (editingNotice) payload.pk = editingNotice.pk;

    const res = await axiosInstance.post(url, payload);

    if (res.data.success) {
      message.success(res.data.message);
      setModalVisible(false);
      loadNotices();
    } else {
      message.error(res.data.message);
    }
  };

  const handleDelete = async (record) => {
    const res = await axiosInstance.post("/notice_api.php?action=delete", {
      pk: record.pk,
    });

    if (res.data.success) {
      message.success("Notice deleted");
      loadNotices();
    }
  };

  const typeColors = {
    info: "blue",
    warning: "orange",
    alert: "red",
    success: "green",
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      render: (t) => <span className="text-blue-600 font-semibold">{t}</span>,
    },
    { title: "Title", dataIndex: "title" },
    {
      title: "Type",
      dataIndex: "notice_type",
      render: (t) => <Tag color={typeColors[t]}>{t.toUpperCase()}</Tag>,
    },
    {
      title: "Target",
      dataIndex: "target_type",
      render: (t) => (
        <Tag color={t === "all" ? "green" : "purple"}>{t.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div className="flex gap-3">
          <Tooltip title="View">
            <EyeOutlined
              className="cursor-pointer text-blue-500"
              onClick={() => openView(record)}
            />
          </Tooltip>

          <Tooltip title="Edit">
            <EditOutlined
              className="cursor-pointer text-orange-500"
              onClick={() => openEdit(record)}
            />
          </Tooltip>

          <Popconfirm
            title="Delete Notice?"
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title="Delete">
              <DeleteOutlined className="cursor-pointer text-red-500" />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="p-4 bg-white rounded-xl shadow-sm border">
      <div className="flex justify-between mb-4">
        <h2 className="text-xl font-semibold">Notice Management</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Send Notice
        </Button>
      </div>

      <Table dataSource={notices} columns={columns} rowKey="pk" />

      {/* Create / Edit Modal */}
      <Modal
        title={editingNotice ? "Edit Notice" : "Send Notice"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="notice_type"
            label="Notice Type"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="info">Info</Select.Option>
              <Select.Option value="warning">Warning</Select.Option>
              <Select.Option value="alert">Alert</Select.Option>
              <Select.Option value="success">Success</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="target_type"
            label="Send To"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="all">All Employees</Select.Option>
              <Select.Option value="employee">Specific Employee</Select.Option>
            </Select>
          </Form.Item>

          {/* Only show when employee is selected */}
          <Form.Item shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue("target_type") === "employee" ? (
                <Form.Item
                  label="Select Employee"
                  name="employee_id"
                  rules={[{ required: true }]}
                >
                  <Select showSearch>
                    {employees.map((e) => (
                      <Select.Option key={e.id} value={e.id}>
                        {e.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal
        open={viewModalVisible}
        title="Notice Details"
        footer={null}
        onCancel={() => setViewModalVisible(false)}
      >
        {viewNotice && (
          <div>
            <p>
              <strong>Title:</strong> {viewNotice.title}
            </p>
            <p>
              <strong>Message:</strong> {viewNotice.message}
            </p>
            <p>
              <strong>Type:</strong>{" "}
              <Tag color={typeColors[viewNotice.notice_type]}>
                {viewNotice.notice_type.toUpperCase()}
              </Tag>
            </p>
            <p>
              <strong>Target:</strong> {viewNotice.target_type.toUpperCase()}
            </p>

            {viewNotice.target_type === "employee" && (
              <p>
                <strong>Employee ID:</strong> {viewNotice.employee_id}
              </p>
            )}

            <p>
              <strong>Created At:</strong> {viewNotice.created_at}
            </p>

            <p>
              <strong>Created By:</strong> {viewNotice.created_by_name}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Notice;
